import React, { useState, useEffect } from 'react';
import './App.css';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import AccountDetail from './components/AccountDetail';
import ProfileDropdown from './components/ProfileDropdown';
import SupportModal from './components/SupportModal';
import Login from './components/Login';
import ErrorBoundary from './components/ErrorBoundary';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { ActionsProvider } from './contexts/ActionsContext';
import { getAccountsForCSM } from './data/dynamicAccountsData';
import { ThemeProvider } from './contexts/ThemeContext';
import UserProfileModal from './components/UserProfileModal';

// Main App Content (protected by authentication)
function AppContent() {
  const [currentAccount, setCurrentAccount] = useState(null);
  const [accounts, setAccounts] = useState({});
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const { isAuthenticated, loading, user } = useAuth();
  
  // Load accounts based on authenticated user
  useEffect(() => {
    if (user && user.email) {
      // Get accounts for the logged-in CSM
      let csmAccounts = getAccountsForCSM(user.email);
      
      // If no accounts found for this CSM, default to George's accounts
      if (Object.keys(csmAccounts).length === 0) {
        console.log('No accounts found for', user.email, '- defaulting to George\'s accounts');
        csmAccounts = getAccountsForCSM('george@relishiq.com');
      }
      
      setAccounts(csmAccounts);
    }
  }, [user]);

  const handleAccountSelect = (accountKey) => {
    setCurrentAccount(accountKey);
  };

  const handleBackToDashboard = () => {
    setCurrentAccount(null);
  };

  const updateActionStatus = (accountKey, meetingIndex, actionIndex) => {
    setAccounts(prevAccounts => {
      const newAccounts = JSON.parse(JSON.stringify(prevAccounts));
      const action = newAccounts[accountKey].meetings[meetingIndex].actions[actionIndex];
      const wasCompleted = action.completed;
      action.completed = !action.completed;
      
      if (!wasCompleted && action.completed) {
        playSuccessSound();
      }
      
      return newAccounts;
    });
  };

  const playSuccessSound = () => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const currentTime = audioContext.currentTime;
    const notes = [523.25, 659.25, 783.99, 1046.50];
    
    notes.forEach((freq, index) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.type = 'sine';
      const startTime = currentTime + (index * 0.05);
      oscillator.frequency.setValueAtTime(freq, startTime);
      
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(0.08, startTime + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + 0.2);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + 0.2);
    });
  };

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <Login />;
  }

  // Show main app if authenticated
  return (
    <div className="app">
      <button 
        className="mobile-menu-toggle"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        aria-label="Toggle menu"
      >
        <span className="menu-icon-bar"></span>
        <span className="menu-icon-bar"></span>
        <span className="menu-icon-bar"></span>
      </button>
      <Sidebar 
        accounts={accounts}
        currentAccount={currentAccount}
        onAccountSelect={handleAccountSelect}
        isMobileOpen={isMobileMenuOpen}
        onMobileClose={() => setIsMobileMenuOpen(false)}
      />
      <main className="main-content">
        <div className="app-header">
          <div className="header-left">
            {currentAccount && (
              <button className="header-back-button" onClick={handleBackToDashboard}>
                ← Back to Dashboard
              </button>
            )}
          </div>
          <ProfileDropdown onSupportClick={() => setIsSupportModalOpen(true)} onOpenProfile={() => setIsProfileOpen(true)} />
        </div>
        {currentAccount ? (
          <AccountDetail 
            account={accounts[currentAccount]}
            accountKey={currentAccount}
            onBack={handleBackToDashboard}
            onActionToggle={updateActionStatus}
          />
        ) : (
          <Dashboard accounts={accounts} />
        )}
      </main>
      <SupportModal 
        isOpen={isSupportModalOpen}
        onClose={() => setIsSupportModalOpen(false)}
      />
      <UserProfileModal isOpen={isProfileOpen} onClose={() => setIsProfileOpen(false)} />
    </div>
  );
}

// Root App with Authentication Provider
function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ActionsProvider>
          <ThemeProvider>
            <AppContent />
          </ThemeProvider>
        </ActionsProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;